import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"

import { BreadcrumbNav } from "@/components/breadcrumb-nav"
import { LoadingState } from "@/components/loading-state"
import { PageHeader } from "@/components/page-header"
import { useContract } from "@/features/contracts/hooks/use-contracts"
import { OrderForm } from "@/features/orders/components/order-form"
import { useUpdateOrder } from "@/features/orders/hooks/use-order-mutations"
import { useOrder } from "@/features/orders/hooks/use-orders"
import { paths } from "@/routes/paths"

export function OrderEditPage() {
  const { orderId = "" } = useParams<{ orderId: string }>()
  const { t } = useTranslation(["orders", "contracts"])
  const navigate = useNavigate()

  const { data: order, isLoading: loadingOrder } = useOrder(orderId)
  const contractId = order?.exporterOrderId ? String(order.exporterOrderId) : ""
  const { data: contract, isLoading: loadingContract } = useContract(contractId)
  const update = useUpdateOrder(contractId, orderId)

  if (loadingOrder || loadingContract || !order || !contract) {
    return <LoadingState />
  }

  return (
    <div className="flex flex-col gap-6">
      <BreadcrumbNav
        crumbs={[
          { label: t("title", { ns: "contracts" }), to: paths.contracts },
          {
            label: contract.contractNumber ?? `#${contract.id}`,
            to: paths.contractDetail(contract.id),
          },
          {
            label: order.orderNumber ?? `#${order.id}`,
            to: paths.orderDetail(order.id),
          },
          { label: t("editOrder") },
        ]}
      />
      <PageHeader title={t("editOrder")} />
      <OrderForm
        contract={contract}
        order={order}
        isSubmitting={update.isPending}
        onSubmit={(payload) => {
          update.mutate(payload, {
            onSuccess: () => {
              navigate(paths.orderDetail(order.id))
            },
          })
        }}
      />
    </div>
  )
}
